import type { ReasoningLevel } from "../auto-reply/thinking.js";
import type { ToolProfileId } from "./tool-policy.js";

export type FleetComposerMode = "agent" | "ask" | "plan" | "debug" | "multitask";

export const FLEET_COMPOSER_MODES: readonly FleetComposerMode[] = [
  "agent",
  "ask",
  "plan",
  "debug",
  "multitask",
] as const;

export function isFleetComposerMode(value: string): value is FleetComposerMode {
  return (FLEET_COMPOSER_MODES as readonly string[]).includes(value);
}

export type FleetComposerModeRun = {
  extraSystemPrompt: string;
  toolProfileOverride?: ToolProfileId;
  toolPolicyAlsoAllow?: string[];
  toolPolicyDeny?: string[];
  reasoningLevel?: ReasoningLevel;
  thinking?: string;
};

const ASK_TOOL_ALLOW = [
  "read",
  "memory_search",
  "memory_get",
  "memory_get_batch",
  "sessions_history",
  "sessions_list",
  "session_status",
  "web_search",
  "web_fetch",
  "image",
] as const;

const ASK_TOOL_DENY = [
  "write",
  "edit",
  "apply_patch",
  "exec",
  "process",
  "bash",
  "sessions_spawn",
  "sessions_send",
  "message",
  "browser",
  "canvas",
  "cron",
  "gateway",
  "nodes",
  "agents_list",
  "email_send",
  "email_receive",
  "whatsapp_login",
  "fleet_ask_user",
  "fleet_todo_read",
  "fleet_todo_write",
  "fleet_todo_read",
] as const;

const REPLY_STYLE = [
  "Reply style (operator-facing): concrete, short, 100% signal.",
  "Lead with status or the next action. Prefer bullets over paragraphs.",
  "No narration, no recap of what you are about to do, no padding.",
  "Do the work with tools; chat only with outcomes, blockers, and decisions that need the operator.",
].join(" ");

const PARALLEL_ORCHESTRATION = [
  "Orchestration: if ≥2 units of work are independent (different files/domains/checks, no shared output dependency), use plan_work(action=propose) to fan out parallel fleet units — unified planner auto-starts the first batch. Use plan_work(action=start) only for draft plans or when propose used autoSpawn=false (or @all when Multitask).",
  "Do NOT use sessions_spawn on main sessions — the unified planner owns fleet concurrency.",
  "Do NOT create shell scripts or audit launcher stubs to fan out agents (scripts/launch_audits.sh and similar are blocked). plan_work is the only parallel entry point.",
  "For 'run 3 audits in parallel', propose exactly 3 audit units (+ optional synth dependent on all three) — no extra scope/evidence preamble units unless the operator asked.",
  "Run sequentially only when a later unit needs an earlier unit's result.",
  "Do not serialize independent work. Avoid duplicate coverage across agents.",
].join(" ");

const MODE_PROMPTS: Record<FleetComposerMode, string> = {
  agent: [
    "Composer mode: Agent.",
    REPLY_STYLE,
    PARALLEL_ORCHESTRATION,
    "During implementation, use fleet_todo_write to keep the operator-facing checklist current.",
    "Before starting a task, call fleet_todo_read to check if it's already completed (avoid duplicate work).",
    "Use fleet_ask_user only for irreversible or scope-fork decisions (deploy, delete data, major approach).",
    "Do not use fleet_ask_user for routine ambiguity, permission checks, or path lookups — prefer chat prose or a stated assumption.",
  ].join("\n"),
  ask: [
    "Composer mode: Ask (read-only).",
    REPLY_STYLE,
    "Answer questions using read-only tools. Do not edit files, run shell commands, spawn sessions, or send messages.",
    "If the user asks for implementation, explain what you would do and suggest switching to Agent mode.",
  ].join("\n"),
  plan: [
    "Composer mode: Plan.",
    REPLY_STYLE,
    "Research the codebase and produce a clear, reviewable plan before writing code.",
    "Outline goals, constraints, risks, and concrete steps. Mark which steps can run in parallel vs must stay sequential.",
    "When 1–3 irreversible or scope-fork decisions block a good plan, use fleet_ask_user (structured options).",
    "Prefer chat prose for soft preferences. Do not block on routine ambiguity.",
    "Do not implement until the user confirms the plan (unless they explicitly ask you to proceed).",
  ].join("\n"),
  debug: [
    "Composer mode: Debug.",
    REPLY_STYLE,
    "Form hypotheses about the bug, gather runtime evidence (logs, repro steps, tests), and verify fixes.",
    "Prefer minimal, targeted changes. State observed evidence and how you validated the fix — no story.",
  ].join("\n"),
  multitask: [
    "Composer mode: Multitask.",
    REPLY_STYLE,
    "The user enabled parallel orchestration (@all).",
    PARALLEL_ORCHESTRATION,
    "Fan out independent subtasks immediately; synthesize only after parallel results return.",
    "Coordinate results and avoid duplicate work across agents.",
  ].join("\n"),
};

export function resolveFleetComposerModeRun(opts: {
  mode: FleetComposerMode | undefined;
}): FleetComposerModeRun {
  const mode = opts.mode ?? "agent";
  const extraSystemPrompt = MODE_PROMPTS[mode];
  switch (mode) {
    case "ask":
      return {
        extraSystemPrompt,
        toolProfileOverride: "minimal",
        toolPolicyAlsoAllow: [...ASK_TOOL_ALLOW],
        toolPolicyDeny: [...ASK_TOOL_DENY],
      };
    case "plan":
      return {
        extraSystemPrompt,
        reasoningLevel: "stream",
        thinking: "high",
      };
    case "debug":
      return {
        extraSystemPrompt,
        reasoningLevel: "stream",
        thinking: "high",
      };
    case "multitask":
      return { extraSystemPrompt };
    case "agent":
    default:
      return { extraSystemPrompt };
  }
}
